import {
  ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, WritableSignal,
  computed, effect, inject, input, output, signal, untracked, viewChild
} from '@angular/core';
import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, Subscription } from 'rxjs';

import { ButtonComponent } from '@libs/front/components/button/button.component';
import { CheckboxComponent } from '@libs/front/components/checkbox/checkbox.component';
import { InputComponent } from '@libs/front/components/input/input.component';
import { PopupComponent } from '@libs/front/components/popup/popup.component';
import { SelectComponent, SelectOption } from '@libs/front/components/select/select.component';

import { Dossier } from 'src/app/bo/dossier';
import { DirectoryType, Fichier } from 'src/app/bo/fichier';
import { Tir } from 'src/app/bo/tir';
import { WorkflowEtapeInstance } from 'src/app/bo/workflowEtapeInstance';
import { BlastService } from 'src/app/services/blast.service';
import { DocumentStorageService } from 'src/app/services/document-storage.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { HttpService } from 'src/app/services/http.service';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';

type SelectableType = 'file' | 'folder';

const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024;
const ROOT_FOLDER_ID = 0;
const PREVIEWABLE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf'];
const FORBIDDEN_NAME_CHARS = ['/', '\\', ':', '*', '?', '"', '<', '>', '|', '.'];

class FileFolder {
  fichiers: Fichier[];
  dossiers: Dossier[];
  id: number;
  apiEndPoint: string;
}

@Component({
  selector: 'app-blast-files',
  standalone: true,
  templateUrl: './blast-files.component.html',
  styleUrls: ['./blast-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    NgTemplateOutlet,
    UpperCasePipe,
    TranslateModule,
    ButtonComponent,
    CheckboxComponent,
    InputComponent,
    PopupComponent,
    SelectComponent,
  ],
})
export class BlastFilesComponent implements OnInit, OnDestroy {
  readonly tirId            = input<number>();
  readonly apiEndPoint      = input<string>('WorkflowSteps');
  readonly securityIsChange = output<WorkflowEtapeInstance>();

  private readonly utilService            = inject(UtilisateurService);
  private readonly blastService           = inject(BlastService);
  private readonly http                   = inject(HttpService);
  private readonly documentStorageService = inject(DocumentStorageService);
  private readonly fonctionsService       = inject(FonctionsService);
  private readonly translate              = inject(TranslateService);
  private readonly keycloakUserService    = inject(KeycloakUtilisateurService);

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  protected readonly rootFolderId      = ROOT_FOLDER_ID;
  protected readonly canEditPermission = this.keycloakUserService.hasRole('manage_site');
  protected readonly permissionOptions: SelectOption[] = [
    { id: 0, label: 'None' },
    { id: 1, label: 'Read' },
    { id: 2, label: 'Read - Write' },
  ];

  // ── Tir / workflow ────────────────────────────────────────────────────────

  protected readonly currentTir    = signal<Tir | null>(null);
  protected readonly workflowSteps = computed<WorkflowEtapeInstance[]>(
    () => this.currentTir()?.workflowInstance?.workflowEtapeInstances ?? []
  );
  protected readonly stepOptions = computed<SelectOption[]>(() =>
    this.workflowSteps().map(step => ({
      id:    step.workflowEtapeInstanceId,
      label: `${step.numeroOrdre}. ${this.getStepTitle(step.nom)}`,
    }))
  );
  protected readonly activeStepId = signal<number | null>(null);
  protected readonly activeStep   = computed(() =>
    this.workflowSteps().find(step => step.workflowEtapeInstanceId === this.activeStepId()) ?? null
  );
  protected readonly stepFolders  = signal<Map<number, Dossier>>(new Map());
  protected readonly activeFolder = computed(() => {
    const stepId = this.activeStepId();
    return stepId != null ? this.stepFolders().get(stepId) ?? null : null;
  });
  protected readonly activeStepIsEmpty = computed(() => {
    const folder = this.activeFolder();
    return !!folder && !folder.fichiers?.length && !folder.sousDossiers?.length;
  });

  // ── Sélection / dépliage / drag & drop ────────────────────────────────────

  protected readonly selectedItems      = signal<Map<number, Set<string>>>(new Map());
  protected readonly expandedSubFolders = signal<Set<number>>(new Set());
  protected readonly updatingPermission = signal<Set<number>>(new Set());
  protected readonly stepDragOver       = signal(false);
  protected readonly subFolderDragOver  = signal<number | null>(null);
  private stepDragCounter = 0;
  private readonly subFolderDragCounters = new Map<number, number>();

  // ── Ajout de fichiers ─────────────────────────────────────────────────────

  protected readonly showUploadPopup      = signal(false);
  protected readonly uploadStepId         = signal<number | null>(null);
  protected readonly uploadFiles          = signal<File[]>([]);
  protected readonly uploadSelected       = signal<Set<number>>(new Set());
  protected readonly uploadDestinations   = signal<(number | null)[]>([]);
  protected readonly uploadGlobalFolderId = signal<number | null>(null);
  protected readonly uploadDragOver       = signal(false);
  protected readonly showUploadProgress   = signal(false);
  protected readonly uploadProgress       = signal(0);
  protected readonly uploadFolder = computed(() => {
    const stepId = this.uploadStepId();
    return stepId != null ? this.stepFolders().get(stepId) ?? null : null;
  });
  protected readonly uploadTotalSize = computed(() => this.uploadFiles().reduce((sum, file) => sum + file.size, 0));
  protected readonly uploadHasDuplicates = computed(() => {
    const existing = this.uploadFolder()?.fichiers;
    if (!existing?.length) return false;
    return this.uploadFiles().some(file => existing.some(f => f.nom === this.normalizeExtension(file.name)));
  });
  protected readonly folderOptions = computed<SelectOption[]>(() => [
    { id: ROOT_FOLDER_ID, label: this.translate.instant('GENERAL_TAB.ROOT_FOLDER') },
    ...(this.uploadFolder()?.sousDossiers ?? []).map(folder => ({ id: folder.dossierId, label: folder.nom })),
  ]);

  // ── Dossiers / renommage / suppression / téléchargement ───────────────────

  protected readonly showAddFolderPopup = signal(false);
  protected readonly newFolderName      = signal('');
  protected readonly folderNameError    = signal(false);
  protected readonly folderNameRequired = signal(false);
  protected readonly folderNameExists   = signal(false);

  protected readonly showRenamePopup    = signal(false);
  protected readonly newName            = signal('');
  protected readonly renameNameError    = signal(false);
  protected readonly renameNameRequired = signal(false);

  protected readonly showDeletePopup    = signal(false);
  protected readonly showSyncErrorPopup = signal(false);
  protected readonly downloadInProgress = signal(false);

  private actionStepId: number | null = null;
  private renamingFile: Fichier | null = null;
  private renamingFolder: Dossier | null = null;
  private deletingFile: Fichier | null = null;
  private deletingFolder: Dossier | null = null;
  private filesRefreshSubscription?: Subscription;

  constructor() {
    effect(() => {
      const tirId = this.tirId();
      if (tirId) {
        untracked(() => this.loadTir(tirId));
      }
    });
  }

  ngOnInit(): void {
    this.filesRefreshSubscription = this.utilService.getFilesRefreshEvent().subscribe(() => this.refreshData());
    this.preventDefaultDragBehavior();
  }

  ngOnDestroy(): void {
    this.filesRefreshSubscription?.unsubscribe();
    this.removeDefaultDragBehavior();
  }

  // ── Chargement ────────────────────────────────────────────────────────────

  /**
   * utilService.currentTir reste la source partagée avec le reste de l'application (comme dans
   * l'ancien app-general) ; le tir n'est rechargé que s'il ne correspond pas à l'URL.
   */
  private loadTir(tirId: number): void {
    const current = this.utilService.currentTir;
    if (current?.tirId === tirId) {
      this.applyTir(current);
      return;
    }
    this.blastService.getBlast(tirId).subscribe((tir: Tir) => {
      this.utilService.currentTir = tir;
      this.applyTir(tir);
    });
  }

  private applyTir(tir: Tir): void {
    this.currentTir.set(tir);
    this.selectedItems.set(new Map());
    this.expandedSubFolders.set(new Set());
    this.activeStepId.set(this.workflowSteps()[0]?.workflowEtapeInstanceId ?? null);
    this.refreshData();
  }

  protected refreshData(): void {
    this.stepFolders.set(new Map());
    this.loadAllStepFolders();
  }

  /** Charge le dossier racine de chaque étape : le compteur de fichiers du rail en dépend. */
  private loadAllStepFolders(): void {
    this.workflowSteps().forEach(step => {
      const stepId = step?.workflowEtapeInstanceId;
      if (!stepId || this.stepFolders().has(stepId)) return;

      this.documentStorageService.getWorkflowStepFolder(stepId).subscribe({
        next: folder => {
          if (!folder) return;
          this.setStepFolder(stepId, folder);
          if (stepId === this.activeStepId() && !this.selectedItems().has(stepId)) {
            this.selectAllInStep(stepId, folder);
          }
        },
        error: (error: HttpErrorResponse) => console.warn(`Unable to load folder for step ${step.nom}:`, error),
      });
    });
  }

  private loadFolder(step: WorkflowEtapeInstance): void {
    const stepId = step.workflowEtapeInstanceId;
    const startLoader = setTimeout(() => this.fonctionsService.loading_screen(true), 300);

    this.documentStorageService.getWorkflowStepFolder(stepId).subscribe({
      next: folder => {
        clearTimeout(startLoader);
        this.fonctionsService.loading_screen(false);
        this.setStepFolder(stepId, folder);
        if (folder && stepId === this.activeStepId()) {
          this.selectAllInStep(stepId, folder);
        }
      },
      error: (error: HttpErrorResponse) => {
        clearTimeout(startLoader);
        this.fonctionsService.loading_screen(false);
        if (error.status === 401) {
          this.translate.get('GLOBAL.NON_AUTORISE').subscribe(trad => this.fonctionsService.launch_toast(false, false, trad));
        }
      },
    });
  }

  private reloadStepFolder(stepId: number): void {
    this.documentStorageService.getWorkflowStepFolder(stepId).subscribe({
      next: folder => { if (folder) this.setStepFolder(stepId, folder); },
      error: (error: HttpErrorResponse) => console.error('Error reloading step folder:', error),
    });
  }

  private setStepFolder(stepId: number, folder: Dossier): void {
    this.stepFolders.update(folders => new Map(folders).set(stepId, folder));
  }

  // ── Étapes ────────────────────────────────────────────────────────────────

  /** Ouvrir une étape pré-sélectionne tout son contenu, comme le dépliage d'une carte dans l'ancien écran. */
  protected selectStep(step: WorkflowEtapeInstance): void {
    const stepId = step.workflowEtapeInstanceId;
    this.activeStepId.set(stepId);

    const folder = this.stepFolders().get(stepId);
    if (folder) {
      this.selectAllInStep(stepId, folder);
    } else {
      this.loadFolder(step);
    }
  }

  protected onStepSelected(option: SelectOption): void {
    const step = this.workflowSteps().find(s => s.workflowEtapeInstanceId === option.id);
    if (step) this.selectStep(step);
  }

  protected getFolderForStep(stepId: number): Dossier | null {
    return this.stepFolders().get(stepId) ?? null;
  }

  protected getStepTitle(stepName: string): string {
    return stepName ? stepName.replace(/^\d+-/, '').trim() : '';
  }

  protected getTotalFilesForStep(step: WorkflowEtapeInstance): number {
    const folder = this.getFolderForStep(step.workflowEtapeInstanceId);
    return folder ? this.countFiles(folder) : 0;
  }

  private countFiles(folder: Dossier): number {
    let count = folder.fichiers?.length ?? 0;
    folder.sousDossiers?.forEach(subFolder => count += this.countFiles(subFolder));
    return count;
  }

  // ── Permissions ───────────────────────────────────────────────────────────

  protected getAccessPermission(step: WorkflowEtapeInstance): string {
    return step.accessPermission === 1 ? 'Read' : step.accessPermission === 2 ? 'Read - Write' : 'None';
  }

  protected isPermissionUpdating(stepId: number): boolean {
    return this.updatingPermission().has(stepId);
  }

  /** Mise à jour optimiste : la valeur précédente est restaurée si l'API refuse. */
  protected onPermissionChange(step: WorkflowEtapeInstance, newValue: number): void {
    const stepId = step.workflowEtapeInstanceId;
    if (!this.canEditPermission || this.isPermissionUpdating(stepId) || +newValue === step.accessPermission) return;

    const previousValue = step.accessPermission;
    step.accessPermission = +newValue;
    this.updatingPermission.update(ids => new Set(ids).add(stepId));

    const release = () => this.updatingPermission.update(ids => {
      const next = new Set(ids);
      next.delete(stepId);
      return next;
    });

    this.http.put(`${this.apiEndPoint()}/${stepId}/UpdateWorkflowStepInstance`, +newValue).subscribe({
      next: () => {
        release();
        this.fonctionsService.launch_toast(true);
        this.securityIsChange.emit(step);
      },
      error: () => {
        step.accessPermission = previousValue;
        release();
        this.fonctionsService.launch_toast(false);
      },
    });
  }

  // ── Sous-dossiers ─────────────────────────────────────────────────────────

  protected toggleSubFolder(folderId: number): void {
    this.expandedSubFolders.update(ids => {
      const next = new Set(ids);
      if (!next.delete(folderId)) next.add(folderId);
      return next;
    });
  }

  protected isSubFolderExpanded(folderId: number): boolean {
    return this.expandedSubFolders().has(folderId);
  }

  // ── Sélection ─────────────────────────────────────────────────────────────

  protected isItemSelected(type: SelectableType, itemId: number, stepId: number): boolean {
    return this.selectedItems().get(stepId)?.has(`${type}:${itemId}`) ?? false;
  }

  /** Cocher un dossier coche tout son contenu ; décocher un fichier décoche son dossier parent. */
  protected toggleItemSelection(type: SelectableType, itemId: number, stepId: number, checked: boolean): void {
    const selections = new Set(this.selectedItems().get(stepId) ?? []);
    const key = `${type}:${itemId}`;

    if (checked) {
      selections.add(key);
      if (type === 'folder') this.selectFilesInFolder(itemId, stepId, selections);
    } else {
      selections.delete(key);
      if (type === 'folder') {
        this.deselectFilesInFolder(itemId, stepId, selections);
      } else {
        const parent = this.findParentFolderOfFile(itemId, stepId);
        if (parent) selections.delete(`folder:${parent.dossierId}`);
      }
    }
    this.selectedItems.update(items => new Map(items).set(stepId, selections));
  }

  protected isAllSelected(stepId: number): boolean {
    const folder = this.getFolderForStep(stepId);
    if (!folder) return false;
    const total = this.getTotalSelectableItems(folder);
    return total > 0 && (this.selectedItems().get(stepId)?.size ?? 0) === total;
  }

  protected toggleSelectAll(stepId: number, checked: boolean): void {
    const folder = this.getFolderForStep(stepId);
    if (!folder) return;
    if (checked) {
      this.selectAllInStep(stepId, folder);
    } else {
      this.selectedItems.update(items => new Map(items).set(stepId, new Set()));
    }
  }

  protected hasSelectedItems(stepId: number): boolean {
    return (this.selectedItems().get(stepId)?.size ?? 0) > 0;
  }

  private selectAllInStep(stepId: number, folder: Dossier): void {
    const selections = new Set<string>();
    this.selectAllItems(folder, selections);
    this.selectedItems.update(items => new Map(items).set(stepId, selections));
  }

  private selectAllItems(folder: Dossier, selections: Set<string>): void {
    folder.fichiers?.forEach(file => selections.add(`file:${file.fichierId}`));
    folder.sousDossiers?.forEach(subFolder => {
      selections.add(`folder:${subFolder.dossierId}`);
      this.selectAllItems(subFolder, selections);
    });
  }

  /** Même périmètre que l'ancien écran : fichiers racine, sous-dossiers directs et leurs fichiers. */
  private getTotalSelectableItems(folder: Dossier): number {
    let count = folder.fichiers?.length ?? 0;
    folder.sousDossiers?.forEach(subFolder => count += 1 + (subFolder.fichiers?.length ?? 0));
    return count;
  }

  private selectFilesInFolder(folderId: number, stepId: number, selections: Set<string>): void {
    const folder = this.findFolderById(folderId, stepId);
    if (!folder) return;
    folder.fichiers?.forEach(file => selections.add(`file:${file.fichierId}`));
    folder.sousDossiers?.forEach(subFolder => this.selectFilesInFolder(subFolder.dossierId, stepId, selections));
  }

  private deselectFilesInFolder(folderId: number, stepId: number, selections: Set<string>): void {
    const folder = this.findFolderById(folderId, stepId);
    if (!folder) return;
    folder.fichiers?.forEach(file => selections.delete(`file:${file.fichierId}`));
    folder.sousDossiers?.forEach(subFolder => this.deselectFilesInFolder(subFolder.dossierId, stepId, selections));
  }

  private findFolderById(folderId: number, stepId: number): Dossier | null {
    const root = this.getFolderForStep(stepId);
    return root ? this.searchFolder(root, folder => folder.dossierId === folderId) : null;
  }

  private findParentFolderOfFile(fileId: number, stepId: number): Dossier | null {
    const root = this.getFolderForStep(stepId);
    return root ? this.searchFolder(root, folder => !!folder.fichiers?.some(file => file.fichierId === fileId)) : null;
  }

  private searchFolder(folder: Dossier, predicate: (folder: Dossier) => boolean): Dossier | null {
    if (predicate(folder)) return folder;
    for (const subFolder of folder.sousDossiers ?? []) {
      const found = this.searchFolder(subFolder, predicate);
      if (found) return found;
    }
    return null;
  }

  private buildSelectedItemsArrays(stepId: number, folder: Dossier): { selectedFiles: Fichier[]; selectedFolders: Dossier[] } {
    const selectedFiles: Fichier[] = [];
    const selectedFolders: Dossier[] = [];
    const selections = this.selectedItems().get(stepId);
    if (selections) {
      this.collectSelectedFoldersAndFiles(folder, selections, selectedFiles, selectedFolders, true);
    }
    return { selectedFiles, selectedFolders };
  }

  /**
   * Un dossier coché part entier ; sinon ses fichiers cochés partent dans une copie partielle du dossier
   * afin que l'archive conserve l'arborescence (les fichiers racine restent à plat).
   */
  private collectSelectedFoldersAndFiles(
    folder: Dossier, selections: Set<string>, selectedFiles: Fichier[], selectedFolders: Dossier[], isRootLevel = false
  ): void {
    if (selections.has(`folder:${folder.dossierId}`)) {
      selectedFolders.push(folder);
      return;
    }

    const filesInFolder = (folder.fichiers ?? []).filter(file => selections.has(`file:${file.fichierId}`));
    if (isRootLevel) {
      selectedFiles.push(...filesInFolder);
    } else if (filesInFolder.length > 0) {
      const partialFolder = new Dossier();
      partialFolder.dossierId       = folder.dossierId;
      partialFolder.nom             = folder.nom;
      partialFolder.dossierParentId = folder.dossierParentId;
      partialFolder.siteId          = folder.siteId;
      partialFolder.projetId        = folder.projetId;
      partialFolder.dossierParent   = folder.dossierParent;
      partialFolder.site            = folder.site;
      partialFolder.projet          = folder.projet;
      partialFolder.isSelected      = folder.isSelected;
      partialFolder.fichiers        = filesInFolder;
      partialFolder.sousDossiers    = [];
      selectedFolders.push(partialFolder);
    }

    folder.sousDossiers?.forEach(subFolder =>
      this.collectSelectedFoldersAndFiles(subFolder, selections, selectedFiles, selectedFolders, false)
    );
  }

  // ── Création de dossier ───────────────────────────────────────────────────

  protected openAddFolder(): void {
    if (!this.activeFolder()) return;
    this.actionStepId = this.activeStepId();
    this.newFolderName.set('');
    this.folderNameError.set(false);
    this.folderNameRequired.set(false);
    this.folderNameExists.set(false);
    this.showAddFolderPopup.set(true);
  }

  protected confirmAddFolder(): void {
    const stepId = this.actionStepId;
    const parent = stepId != null ? this.getFolderForStep(stepId) : null;
    if (stepId == null || !parent) return;

    const name = this.newFolderName();
    this.folderNameExists.set(parent.sousDossiers?.some(folder => folder.nom === name.trim()) ?? false);
    if (!this.validateName(name, this.folderNameError, this.folderNameRequired) || this.folderNameExists()) return;

    const newFolder = new Dossier();
    newFolder.dossierParentId = parent.dossierId;
    newFolder.nom = name;

    this.fonctionsService.loading_screen(true);
    this.http.post<Dossier, Dossier>(`${this.apiEndPoint()}/${stepId}/Folders/Add`, newFolder).subscribe({
      next: (folder: Dossier) => {
        this.fonctionsService.loading_screen(false);
        this.setStepFolder(stepId, folder);
        this.showAddFolderPopup.set(false);
      },
      error: () => this.fonctionsService.loading_screen(false),
    });
  }

  private validateName(value: string, errorFlag: WritableSignal<boolean>, requiredFlag: WritableSignal<boolean>): boolean {
    requiredFlag.set(value == null || value.trim() === '');
    errorFlag.set([...(value ?? '')].some(char => FORBIDDEN_NAME_CHARS.includes(char)));
    return !requiredFlag() && !errorFlag();
  }

  // ── Renommage ─────────────────────────────────────────────────────────────

  protected renameFile(file: Fichier, stepId: number): void {
    this.openRename(stepId, file, null, file.nom.split('.')[0]);
  }

  protected renameFolder(folder: Dossier, stepId: number): void {
    this.openRename(stepId, null, folder, folder.nom);
  }

  private openRename(stepId: number, file: Fichier | null, folder: Dossier | null, name: string): void {
    this.actionStepId   = stepId;
    this.renamingFile   = file;
    this.renamingFolder = folder;
    this.newName.set(name);
    this.renameNameError.set(false);
    this.renameNameRequired.set(false);
    this.showRenamePopup.set(true);
  }

  /** En cas d'échec, l'élément a pu être supprimé entre-temps : on resynchronise et on prévient l'utilisateur. */
  protected confirmRename(): void {
    const stepId = this.actionStepId;
    const name = (this.newName() ?? '').split('.')[0];
    this.newName.set(name);
    if (stepId == null || !this.validateName(name, this.renameNameError, this.renameNameRequired)) return;

    const request: Observable<Dossier> = this.renamingFile
      ? this.documentStorageService.renameFile(this.apiEndPoint(), stepId, this.renamingFile.fichierId, this.tirId(), name)
      : this.documentStorageService.renameFolder(this.apiEndPoint(), stepId, this.renamingFolder!.dossierId, name);

    this.fonctionsService.loading_screen(true);
    request.subscribe({
      next: () => {
        this.reloadStepFolder(stepId);
        this.closeRename();
        this.fonctionsService.loading_screen(false);
      },
      error: () => {
        this.reloadStepFolder(stepId);
        this.closeRename();
        this.fonctionsService.loading_screen(false);
        this.showSyncErrorPopup.set(true);
      },
    });
  }

  protected closeRename(): void {
    this.showRenamePopup.set(false);
    this.renamingFile   = null;
    this.renamingFolder = null;
  }

  // ── Suppression ───────────────────────────────────────────────────────────

  protected deleteFile(file: Fichier, stepId: number): void {
    this.openDelete(stepId, file, null);
  }

  protected deleteFolder(folder: Dossier, stepId: number): void {
    this.openDelete(stepId, null, folder);
  }

  private openDelete(stepId: number, file: Fichier | null, folder: Dossier | null): void {
    this.actionStepId   = stepId;
    this.deletingFile   = file;
    this.deletingFolder = folder;
    this.showDeletePopup.set(true);
  }

  protected confirmDelete(): void {
    const stepId = this.actionStepId;
    const requests: Observable<unknown>[] = [];
    if (this.deletingFolder) {
      requests.push(this.http.post(`${this.apiEndPoint()}/${stepId}/Folders/Delete`, [this.deletingFolder]));
    }
    if (this.deletingFile) {
      requests.push(this.http.post(`${this.apiEndPoint()}/${stepId}/Files/Delete/${this.tirId()}`, [this.deletingFile]));
    }
    if (stepId == null || requests.length === 0) {
      this.closeDelete();
      return;
    }

    this.fonctionsService.loading_screen(true);
    forkJoin(requests).subscribe({
      next: () => {
        this.reloadStepFolder(stepId);
        this.closeDelete();
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(true, false);
      },
      error: () => {
        this.closeDelete();
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(false);
      },
    });
  }

  protected closeDelete(): void {
    this.showDeletePopup.set(false);
    this.deletingFile   = null;
    this.deletingFolder = null;
  }

  // ── Ajout de fichiers (popup) ─────────────────────────────────────────────

  protected openUploadPopup(): void {
    const stepId = this.activeStepId();
    if (stepId == null || !this.activeFolder()) return;
    this.resetUploadState();
    this.uploadStepId.set(stepId);
    this.showUploadPopup.set(true);
  }

  protected cancelUpload(): void {
    this.showUploadPopup.set(false);
    this.resetUploadState();
  }

  private resetUploadState(): void {
    this.uploadFiles.set([]);
    this.uploadSelected.set(new Set());
    this.uploadDestinations.set([]);
    this.uploadGlobalFolderId.set(null);
    this.uploadDragOver.set(false);
    const input = this.fileInput()?.nativeElement;
    if (input) input.value = '';
  }

  protected triggerFileInput(): void {
    this.fileInput()?.nativeElement.click();
  }

  protected onFileInputChange(event: Event): void {
    const fileList = (event.target as HTMLInputElement).files;
    if (fileList?.length) this.processFiles(fileList);
  }

  protected onUploadDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.uploadDragOver.set(true);
  }

  protected onUploadDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.uploadDragOver.set(false);
  }

  protected onUploadDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.uploadDragOver.set(false);
    const fileList = event.dataTransfer?.files;
    if (fileList?.length) this.processFiles(fileList);
  }

  /**
   * Filtre les fichiers vides, trop lourds (≥ 2 Go) ou déjà ajoutés, puis les ajoute cochés :
   * un fichier coché part dans le dossier choisi globalement.
   */
  private processFiles(fileList: FileList): void {
    const oversized: File[] = [];
    const valid: File[] = [];
    Array.from(fileList).forEach(file => {
      if (file.size === 0) return;
      (file.size >= MAX_FILE_SIZE ? oversized : valid).push(file);
    });

    if (oversized.length > 0) {
      const maxSize = Math.round(MAX_FILE_SIZE / (1024 * 1024));
      const message$ = oversized.length === 1
        ? this.translate.get('FILE_EXPLORER.FILE_TOO_LARGE_SINGLE', { fileName: oversized[0].name, maxSize })
        : this.translate.get('FILE_EXPLORER.FILE_TOO_LARGE_MULTIPLE', { count: oversized.length, maxSize, fileNames: oversized.map(f => f.name).join(', ') });
      message$.subscribe(message => this.fonctionsService.launch_toast(false, false, message));
    }

    if (valid.length === 0) {
      if (oversized.length === 0) {
        this.translate.get('FILE_EXPLORER.NO_VALID_FILES').subscribe(message => this.fonctionsService.launch_toast(false, false, message));
      }
      return;
    }

    const existingNames = this.uploadFiles().map(file => file.name.toLowerCase());
    const unique = valid.filter(file => {
      const isDuplicate = existingNames.includes(file.name.toLowerCase());
      if (isDuplicate) {
        this.translate.get('FILE_EXPLORER.DUPLICATE_FILE_SKIPPED', { fileName: file.name })
          .subscribe(message => this.fonctionsService.launch_toast(false, false, message));
      }
      return !isDuplicate;
    });

    if (unique.length === 0) {
      this.translate.get('FILE_EXPLORER.ALL_FILES_DUPLICATES').subscribe(message => this.fonctionsService.launch_toast(false, false, message));
      return;
    }

    const previousCount = this.uploadFiles().length;
    this.uploadFiles.update(files => [...files, ...unique]);
    this.uploadSelected.update(selected => {
      const next = new Set(selected);
      unique.forEach((_, i) => next.add(previousCount + i));
      return next;
    });
    this.uploadDestinations.update(destinations => [...destinations, ...unique.map(() => null)]);
  }

  protected areAllUploadFilesSelected(): boolean {
    return this.uploadFiles().length > 0 && this.uploadSelected().size === this.uploadFiles().length;
  }

  protected toggleUploadFile(index: number, checked: boolean): void {
    this.uploadSelected.update(selected => {
      const next = new Set(selected);
      if (checked) next.add(index); else next.delete(index);
      return next;
    });
    this.uploadDestinations.update(destinations => destinations.map((dest, i) => i === index ? null : dest));
  }

  protected toggleAllUploadFiles(checked: boolean): void {
    this.uploadSelected.set(checked ? new Set(this.uploadFiles().map((_, i) => i)) : new Set());
    this.uploadDestinations.update(destinations => destinations.map(() => null));
  }

  /** Les index de sélection et de destination sont décalés pour rester alignés sur la liste. */
  protected removeUploadFile(index: number): void {
    this.uploadFiles.update(files => files.filter((_, i) => i !== index));
    this.uploadSelected.update(selected => {
      const next = new Set<number>();
      selected.forEach(i => { if (i !== index) next.add(i > index ? i - 1 : i); });
      return next;
    });
    this.uploadDestinations.update(destinations => destinations.filter((_, i) => i !== index));
  }

  protected onGlobalFolderSelected(option: SelectOption): void {
    this.uploadGlobalFolderId.set(option.id === ROOT_FOLDER_ID ? null : option.id);
  }

  protected onFileDestinationChange(index: number, value: string): void {
    const folderId = +value;
    this.uploadDestinations.update(destinations =>
      destinations.map((dest, i) => i === index ? (folderId === ROOT_FOLDER_ID ? null : folderId) : dest)
    );
  }

  protected confirmUpload(): void {
    const stepId = this.uploadStepId();
    const folder = this.uploadFolder();
    if (stepId == null || !folder) return;
    this.uploadSelectedFiles(stepId, folder);
  }

  /** Dépôt direct sur l'étape ou un sous-dossier : envoi immédiat, sans passer par la popup. */
  private uploadDroppedFiles(fileList: FileList, stepId: number, folderId?: number): void {
    const folder = this.getFolderForStep(stepId);
    if (!folder) {
      this.fonctionsService.launch_toast(false, false, 'Unable to access step folder');
      return;
    }

    this.resetUploadState();
    this.uploadStepId.set(stepId);
    this.processFiles(fileList);
    if (this.uploadFiles().length === 0) return;

    this.uploadGlobalFolderId.set(folderId ?? null);
    this.uploadSelectedFiles(stepId, folder);
  }

  /** Une requête par dossier de destination ; chaque réponse renvoie le dossier racine de l'étape à jour. */
  private uploadSelectedFiles(stepId: number, rootFolder: Dossier): void {
    const files = this.uploadFiles();
    if (files.length === 0) {
      this.fonctionsService.launch_toast(false);
      return;
    }

    this.showUploadPopup.set(false);
    this.uploadProgress.set(0);
    this.showUploadProgress.set(true);

    const filesByFolder = new Map<number, File[]>();
    files.forEach((file, index) => {
      const destination = (this.uploadSelected().has(index) ? this.uploadGlobalFolderId() : this.uploadDestinations()[index])
        || rootFolder.dossierId;
      filesByFolder.set(destination, [...(filesByFolder.get(destination) ?? []), file]);
    });

    const uploads = Array.from(filesByFolder.entries()).map(([folderId, folderFiles]) =>
      new Promise<Dossier | null>((resolve, reject) => {
        const formData = new FormData();
        folderFiles.forEach(file => formData.append(file.name, file));
        formData.append('dossierId', folderId.toString());

        this.http.request('POST', `${this.apiEndPoint()}/${stepId}/Files/Upload`, formData).subscribe({
          next: (event: any) => {
            if (event.type === HttpEventType.UploadProgress && event.total) {
              this.uploadProgress.set(Math.round(event.loaded / event.total * 100));
            }
            if (event.type === HttpEventType.Response) {
              resolve(event.body as Dossier);
            }
          },
          error: error => reject(error),
        });
      })
    );

    Promise.allSettled(uploads).then(results => {
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value) this.setStepFolder(stepId, result.value);
      });
      const errorCount = results.filter(result => result.status === 'rejected').length;

      this.showUploadProgress.set(false);
      this.resetUploadState();

      if (errorCount === 0) {
        this.fonctionsService.launch_toast(true, false);
      } else {
        this.fonctionsService.launch_toast(false, false, `Upload completed with ${errorCount} error(s)`);
      }
    });
  }

  // ── Drag & drop sur l'étape active ────────────────────────────────────────

  private preventDefaultDragBehavior(): void {
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(name => document.addEventListener(name, this.preventDefaults, false));
  }

  private removeDefaultDragBehavior(): void {
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(name => document.removeEventListener(name, this.preventDefaults, false));
  }

  private readonly preventDefaults = (event: Event) => {
    const target = event.target as Element;
    if (!target.closest?.('.step-drag-zone')) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  protected onStepDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.stepDragCounter++ === 0) this.stepDragOver.set(true);
  }

  protected onStepDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  protected onStepDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.stepDragCounter = Math.max(0, this.stepDragCounter - 1);
    if (this.stepDragCounter === 0) this.stepDragOver.set(false);
  }

  protected onStepDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.resetStepDrag();

    const target = event.target as Element;
    if (target.closest('button, input, select, app-button, app-checkbox, app-select')) return;

    const stepId = this.activeStepId();
    const fileList = event.dataTransfer?.files;
    if (fileList?.length && stepId != null) this.uploadDroppedFiles(fileList, stepId);
  }

  protected onSubFolderDragEnter(event: DragEvent, folderId: number): void {
    event.preventDefault();
    event.stopPropagation();
    const count = this.subFolderDragCounters.get(folderId) ?? 0;
    this.subFolderDragCounters.set(folderId, count + 1);
    if (count === 0) this.subFolderDragOver.set(folderId);
  }

  protected onSubFolderDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  protected onSubFolderDragLeave(event: DragEvent, folderId: number): void {
    event.preventDefault();
    event.stopPropagation();
    const count = Math.max(0, (this.subFolderDragCounters.get(folderId) ?? 0) - 1);
    this.subFolderDragCounters.set(folderId, count);
    if (count === 0 && this.subFolderDragOver() === folderId) this.subFolderDragOver.set(null);
  }

  /** Le survol de l'étape parente est aussi réinitialisé : ses compteurs ne voient pas les événements du sous-dossier. */
  protected onSubFolderDrop(event: DragEvent, stepId: number, folderId: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.subFolderDragCounters.set(folderId, 0);
    this.subFolderDragOver.set(null);
    this.resetStepDrag();

    const fileList = event.dataTransfer?.files;
    if (fileList?.length) this.uploadDroppedFiles(fileList, stepId, folderId);
  }

  private resetStepDrag(): void {
    this.stepDragCounter = 0;
    this.stepDragOver.set(false);
  }

  // ── Téléchargement ────────────────────────────────────────────────────────

  protected exportStep(): void {
    const stepId = this.activeStepId();
    const folder = this.activeFolder();
    if (stepId == null || !folder) return;

    if (!this.hasSelectedItems(stepId)) {
      this.fonctionsService.launch_toast(false);
      return;
    }
    const { selectedFiles, selectedFolders } = this.buildSelectedItemsArrays(stepId, folder);
    this.download(selectedFiles, selectedFolders, folder.nom, stepId);
  }

  /** Archive de toutes les étapes ; les dossiers pas encore chargés sont récupérés avant l'export. */
  protected exportAllDocuments(): void {
    const tir = this.currentTir();
    const steps = this.workflowSteps();
    if (!tir || steps.length === 0) {
      this.fonctionsService.launch_toast(false);
      return;
    }

    const startLoader = setTimeout(() => this.fonctionsService.loading_screen(true), 300);
    const folders: Dossier[] = [];

    const loads = steps.map(step => new Promise<void>((resolve, reject) => {
      const stepId = step.workflowEtapeInstanceId;
      const folder = this.getFolderForStep(stepId);
      if (folder) {
        folder.stepId = stepId;
        folders.push(folder);
        resolve();
        return;
      }
      this.documentStorageService.getWorkflowStepFolder(stepId).subscribe({
        next: loaded => {
          if (!loaded) {
            reject(`Unable to load folder for step ${step.nom}`);
            return;
          }
          this.setStepFolder(stepId, loaded);
          folders.push(loaded);
          resolve();
        },
        error: (error: HttpErrorResponse) => reject(`Error loading step ${step.nom}: ${error.message}`),
      });
    }));

    Promise.allSettled(loads).then(results => {
      clearTimeout(startLoader);
      this.fonctionsService.loading_screen(false);

      if (results.some(result => result.status === 'rejected')) {
        this.fonctionsService.launch_toast(false);
        return;
      }
      this.download([], folders, 'All_Documents', tir.workflowInstance.workflowInstanceId);
    });
  }

  protected downloadFile(file: Fichier, stepId: number): void {
    this.download([file], [], file.nom, stepId);
  }

  /** Un fichier seul est téléchargé tel quel ; toute autre combinaison part en archive zip nommée d'après le tir. */
  private download(files: Fichier[], folders: Dossier[], archiveName: string, stepId: number): void {
    if (files.length === 1 && folders.length === 0) {
      this.openFile(files[0], stepId);
      return;
    }

    const body = new FileFolder();
    body.fichiers = files;
    body.dossiers = folders;
    const fileCategory = this.documentStorageService.getCurrentFileCategory(this.apiEndPoint());

    this.downloadInProgress.set(true);
    this.documentStorageService.downloadFiles(body, stepId, this.tirId(), fileCategory).subscribe({
      next: (result: BlobPart) => {
        this.triggerDownload(new Blob([result], { type: 'application/zip' }), this.fonctionsService.buildArchiveName(this.currentTir()?.nom, archiveName));
        this.downloadInProgress.set(false);
      },
      error: () => this.downloadInProgress.set(false),
    });
  }

  private openFile(file: Fichier, stepId: number): void {
    this.fonctionsService.loading_screen(true);

    if (file.storedInS3) {
      const s3key = `${file.siteId}/${this.tirId()}/workflow/${stepId}/${file.nom}`;
      this.documentStorageService.getDownloadUrl(s3key, file.fichierId, file.siteId, DirectoryType.USERFILES, true).subscribe({
        next: result => {
          if (result?.downloadUrl) {
            const link = document.createElement('a');
            link.href = result.downloadUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          this.fonctionsService.loading_screen(false);
        },
        error: (error: HttpErrorResponse) => this.handleError(error),
      });
      return;
    }

    const fileFolder = new FileFolder();
    fileFolder.fichiers = [file];
    this.documentStorageService.checkFilesExist(fileFolder).subscribe({
      next: exists => {
        if (!exists) {
          this.fonctionsService.launch_toast(false, false, 'File not found');
          this.fonctionsService.loading_screen(false);
          return;
        }
        this.http.downloadFile(`Files/${file.fichierId}/Open/${file.siteId}?fileType=${DirectoryType.USERFILES}`).subscribe({
          next: response => {
            if (!response) {
              this.fonctionsService.launch_toast(false, false, 'Error during file download');
              this.fonctionsService.loading_screen(false);
              return;
            }
            this.triggerDownload(new Blob([response.body], { type: 'base64' }), file.nom);
            setTimeout(() => this.fonctionsService.loading_screen(false), 1000);
          },
          error: (error: HttpErrorResponse) => this.handleError(error),
        });
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  private triggerDownload(blob: Blob, fileName: string): void {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => link.remove(), 100);
  }

  protected openFileInNewWindow(file: Fichier, event: Event, stepId: number): void {
    event.stopPropagation();

    const notifyNotFound = (error: HttpErrorResponse) => {
      if (error.status === 404) {
        this.translate.get('GLOBAL.NO_FILE_FOUND').subscribe(trad => this.fonctionsService.launch_toast(false, false, trad));
      }
    };

    if (file.storedInS3) {
      const s3key = `${file.siteId}/${this.tirId()}/workflow/${stepId}/${file.nom}`;
      this.documentStorageService.getDownloadUrl(s3key, file.fichierId, file.siteId, DirectoryType.USERFILES).subscribe({
        next: result => { if (result?.downloadUrl) window.open(result.downloadUrl, '_blank'); },
        error: notifyNotFound,
      });
      return;
    }

    this.documentStorageService.openFileInNewWindow(`Files/${file.fichierId}/Open/${file.siteId}/?fileType=2`).subscribe({
      next: blob => window.open(URL.createObjectURL(blob), '_blank'),
      error: notifyNotFound,
    });
  }

  private handleError(error: HttpErrorResponse): void {
    console.error('Error:', error);
    this.fonctionsService.launch_toast(false, false, 'An error occurred');
    this.fonctionsService.loading_screen(false);
  }

  // ── Affichage ─────────────────────────────────────────────────────────────

  protected canPreview(file: Fichier): boolean {
    return PREVIEWABLE_EXTENSIONS.includes(this.getFileExtension(file.nom).toLowerCase());
  }

  protected getFileExtension(fileName: string): string {
    return fileName.slice((fileName.lastIndexOf('.') - 1 >>> 0) + 2);
  }

  protected formatFileSize(sizeInBytes: number): string {
    if (!sizeInBytes) return '0Mo';
    return Math.round(sizeInBytes / (1024 * 1024)) + 'Mo';
  }

  protected formatFileDate(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    return `${this.pad(d.getDate())}/${this.pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  /** Infobulle des métadonnées : heure de dépôt et auteur, affichés en ligne dans l'ancien écran. */
  protected getFileTooltip(file: Fichier): string {
    if (!file.dateCreation) return file.uploadedByUsername ?? '';
    const d = new Date(file.dateCreation);
    const dateTime = `${this.formatFileDate(file.dateCreation)} ${this.pad(d.getHours())}:${this.pad(d.getMinutes())}`;
    return file.uploadedByUsername ? `${dateTime} - ${file.uploadedByUsername}` : dateTime;
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  private normalizeExtension(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex === -1 ? fileName : fileName.substring(0, dotIndex) + fileName.substring(dotIndex).toLowerCase();
  }
}
